/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NavigationModule
 */

import {
    ChangeDetectionStrategy,
    Component,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import * as StateNames from 'state-names';
import * as globalL10n from 'global-l10n';
import { INavState } from '../../navigation.types';
import { NavigationService } from '../../services/navigation.service';
import * as l10n from './operations-vertical-nav.component.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Vertical Navigation component for the Operations pages.
 * @author alextsg
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'operations-vertical-nav',
    template: '<vertical-nav [states]="operationsStates"></vertical-nav>',
})
export class OperationsVerticalNavComponent {
    /**
     * List of navigation states and children that should be shown in the nav.
     */
    public operationsStates: INavState[] = [];

    constructor(
        navigationService: NavigationService,
        l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        const labelsHash = {
            [StateNames.OPERATIONS_ALERTS]: {
                icon: 'bubble-exclamation',
                label: l10nService.getMessage(globalL10nKeys.alertsLabel),
            },
            [StateNames.ALERTS_ALL_ALERTS]: {
                label: l10nService.getMessage(l10nKeys.allAlertsLabel),
            },
            [StateNames.ALERTS_ALERT_CONFIG]: {
                label: l10nService.getMessage(l10nKeys.alertConfigLabel),
            },
            [StateNames.ALERTS_ALERT_ACTIONS]: {
                label: l10nService.getMessage(l10nKeys.alertActionsLabel),
            },
            [StateNames.OPERATIONS_EVENTS]: {
                icon: 'event',
                label: l10nService.getMessage(globalL10nKeys.eventsLabel),
            },
            [StateNames.EVENTS_EVENTS_LIST]: {
                label: l10nService.getMessage(l10nKeys.allEventsLabel),
            },
            [StateNames.EVENTS_CONFIGURATION]: {
                label: l10nService.getMessage(l10nKeys.configAuditTrailLabel),
            },
            [StateNames.OPERATIONS_SERVICES]: {
                icon: 'bell',
                label: l10nService.getMessage(l10nKeys.notificationsLabel),
            },
            [StateNames.SERVICES_SYSLOG_LIST]: {
                label: l10nService.getMessage(globalL10nKeys.syslogLabel),
            },
            [StateNames.SERVICES_EMAIL_LIST]: {
                label: l10nService.getMessage(globalL10nKeys.emailLabel),
            },
            [StateNames.SERVICES_SNMP_LIST]: {
                label: l10nService.getMessage(l10nKeys.snmpTrapLabel),
            },
            [StateNames.OPERATIONS_TRAFFIC_CAPTURE]: {
                icon: 'cloud-traffic',
                label: l10nService.getMessage(l10nKeys.trafficCaptureLabel),
            },
            [StateNames.OPERATIONS_REPORTS]: {
                icon: 'list',
                label: l10nService.getMessage(l10nKeys.reportsLabel),
            },
        };

        this.operationsStates = navigationService.getChildren(StateNames.OPERATIONS)
            .map(state => {
                const { label, icon } = labelsHash[state.name];

                return {
                    ...state,
                    label,
                    icon,
                    children: state.children?.map(childState => {
                        const childStateProps = labelsHash[childState.name];

                        return {
                            ...childState,
                            label: childStateProps?.label,
                            icon: childStateProps?.icon,
                        };
                    }),
                };
            });
    }
}
